import type { UseInfiniteQueryOptions, UseQueryOptions } from '@tanstack/react-query';
import type { AxiosError, AxiosRequestConfig } from 'axios';
import { useCallback, useMemo } from 'react';
import { useInfiniteQuery, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { KEYS, repository } from '@repositories/Repository';
import type {
  ProductSubscriptionActionType,
  ProductSubscriptionFilter,
} from '@repositories/productSubscriptionRepository/productSubscription.types';
import type ISubscriptionRepository from '@repositories/subscriptionRepository/ISubscriptionRepository';
import type {
  Filters,
  Subscription,
  SubscriptionDailyUsageFilter,
  SubscriptionList,
  SubscriptionMonthlyUsageFilter,
  SubscriptionUserCreateRq,
  SubscriptionUserDTO,
  SubscriptionUserFilter,
  SubscriptionUserStatDTO,
  SubscriptionUserUpdateRq,
  SubscriptionUsersActivityStats,
  ExternalSubscriptionCreateRq,
  RemoteSubscrptionUserCreateDTO,
  MonthlySubscriptionUserDTO,
  SubscriptionMonthlyUserFilter,
  UserSubscriptionUpdateDTO,
  SubscriptionMemoUpdateDTO,
} from '@repositories/subscriptionRepository/subscription.types';
import type { DailyUsageDTO, MonthlyUsageDTO } from '@repositories/usage';
import type { Page } from '@type/Page';
import { QueryUtil } from '@utils/queryUtil';
import { SubscriptionListModel } from '@models/SubscriptionListModel';
import { SubscriptionModel } from '@models/SubscriptionModel';
import { SubscriptionUserModel } from '@models/SubscriptionUserModel';
import { MonthlySubscriptionUserModel, SubscriptionUserStatModel } from '@models/subscription';
import { DailyUsageModel } from '@models/usageModels/DailyUsageModel';
import { MonthlyUsageModel } from '@models/usageModels/MonthlyUsageModel';
import { tenantMemberQueryKey } from './useTenant';

const sbRepo = repository.get<ISubscriptionRepository>(KEYS.SUBSCRIPTION_REPOSITORY);
export const sbQueryKey = {
  all: ['subscription'] as const,
  lists: () => [...sbQueryKey.all, 'list'] as const,
  list: (filter?: Filters) => [...sbQueryKey.lists(), { ...filter }] as const,
  listInfinite: (filter?: Filters) => [...sbQueryKey.list(filter), 'infinite'] as const,
  details: () => [...sbQueryKey.all, 'detail'] as const,
  detail: (id: string) => [...sbQueryKey.details(), id] as const,
  dailyUsage: (id: string, filter: SubscriptionDailyUsageFilter) =>
    [...sbQueryKey.detail(id), 'dailyUsage', { ...filter }] as const,
  dailyUsageInfinite: (id: string, filter: SubscriptionDailyUsageFilter) =>
    [...sbQueryKey.dailyUsage(id, filter), 'infinite'] as const,
  monthlyUsage: (id: string, filter: SubscriptionMonthlyUsageFilter) =>
    [...sbQueryKey.detail(id), 'monthlyUsage', { ...filter }] as const,
  monthlyUsageInfinite: (id: string, filter: SubscriptionMonthlyUsageFilter) =>
    [...sbQueryKey.monthlyUsage(id, filter), 'infinite'] as const,
  monthlyUser: (id: string, filter: SubscriptionMonthlyUserFilter) =>
    [...sbQueryKey.detail(id), 'monthlyUser', { ...filter }] as const,
  userStatusCountSummary: (id: string) => [...sbQueryKey.detail(id), 'userStatusCountSummary'] as const,
  userStatus: (id: string) => [...sbQueryKey.detail(id), 'userStatus'] as const,
  usersActivityStats: (id: string) => [...sbQueryKey.detail(id), 'usersActivityStats'] as const,
};

const subscriptionUserQueryKey = {
  all: ['SubscriptionUser'] as const,
  lists: () => [...subscriptionUserQueryKey.all, 'list'] as const,
  list: (subscriptionId: string, filters: SubscriptionUserFilter) =>
    [...subscriptionUserQueryKey.lists(), subscriptionId, { ...filters }] as const,
  listInfinite: (subscriptionId: string, filters: SubscriptionUserFilter) =>
    [...subscriptionUserQueryKey.list(subscriptionId, filters), 'infinite'] as const,
  details: () => [...subscriptionUserQueryKey.all, 'detail'] as const,
  detail: (subscriptionId: string, userLoginId: string) =>
    [...subscriptionUserQueryKey.details(), subscriptionId, userLoginId] as const,
};

export const useGetSubscriptionList = (
  queries?: Filters,
  options?: Omit<UseQueryOptions<Page<SubscriptionList>, AxiosError, Page<SubscriptionListModel>>, 'queryKey' | 'queryFn' | 'select'>,
) => {
  const selectSubscriptionList = useCallback(
    (data: Page<SubscriptionList>): Page<SubscriptionListModel> => ({
      ...data,
      content: data.content.map(sb => new SubscriptionListModel(sb)),
    }),
    [],
  );

  const result = useQuery<Page<SubscriptionList>, AxiosError, Page<SubscriptionListModel>>({
    queryKey: sbQueryKey.list(queries),
    queryFn: () => sbRepo.getList(queries),
    select: selectSubscriptionList,
    ...options,
    cacheTime: 50000,
    staleTime: 30000,
  });

  const initialData = useMemo(
    () => ({
      content: [] as SubscriptionListModel[],
      totalElements: 0,
      totalPages: 0,
    }),
    [],
  );
  return {
    ...result,
    data: result.data ?? initialData,
  };
};

export const useGetSubscriptionListInfinite = (
  queries?: Omit<Filters, 'page'>,
  options?: UseInfiniteQueryOptions<Page<SubscriptionListModel>>,
) =>
  useInfiniteQuery({
    queryKey: sbQueryKey.listInfinite(queries),
    queryFn: async ({ pageParam = 0 }) => {
      const result = await sbRepo.getList({ ...queries, page: pageParam });
      return {
        ...result,
        content: result.content.map(dto => new SubscriptionListModel(dto)),
        pageParam,
      };
    },
    ...options,
    getNextPageParam: lastPage => (lastPage.last ? undefined : lastPage.number + 1),
  });

/**
 * 여러 softwareId(앱)의 하위 구독 목록을 한 번에 조회.
 * 앱 단위 그룹 테이블에서 펼쳤을 때 각 앱의 구독 목록을 받아오는 용도.
 *
 * `includesBillingStat: true`를 항상 포함 — 연 비용 표시에 필요.
 * BE는 false(기본값)일 때 성능 최적화 차원에서 billingStat을 응답에서 생략할 수 있음.
 */
const SUBSCRIPTIONS_PER_GROUP_PAGE_SIZE = 100;
const SUBSCRIPTIONS_BY_SOFTWARE_STALE_TIME_MS = 10 * 60 * 1000;

type ProductListScopeFilter = ProductSubscriptionFilter & { filterType?: string };

/**
 * 앱 목록 필터의 BE ActionType → 구독 목록 API 기존 `actionType` 값.
 * 단일 선택일 때만 전달(다중 OR은 제품 API 전용).
 */
const toSubscriptionActionType = (
  types?: ProductSubscriptionActionType[],
): Filters['actionType'] => {
  if (!types?.length || types.length !== 1) return undefined;
  if (types[0] === 'USAGE_WORKFLOW_FAILED') return 'CONNECTION_ERROR';
  if (types[0] === 'OPTIMIZABLE_LICENSE') return 'ORPHANED_MEMBER';
  return undefined;
};

/**
 * 제품(앱) 단위 목록에 적용된 overview·헤더 필터를 구독 단위 `GET /subscriptions` 호출에 맞게 변환.
 * `sort`·`page`·`size`는 그룹 펼침 전용 값으로 덮어쓰므로 제외한다.
 */
const buildSubscriptionListParamsForProductScope = (
  softwareId: string,
  listScope?: ProductListScopeFilter,
): Filters => {
  const base: Filters = {
    softwareId,
    size: SUBSCRIPTIONS_PER_GROUP_PAGE_SIZE,
    includesBillingStat: true,
  };
  if (!listScope) return base;

  // ProductSubscriptionFilter.status: SubscriptionStatus[] → Filters.status: string | string[] 호환.
  return {
    ...base,
    keyword: listScope.keyword,
    status: listScope.status,
    productVendorName: listScope.productVendorName,
    nextExpiryDateFrom: listScope.nextExpiryDateFrom,
    nextExpiryDateTo: listScope.nextExpiryDateTo,
    utilizationRateFrom: listScope.utilizationRateFrom,
    utilizationRateTo: listScope.utilizationRateTo,
    annualPaymentFrom: listScope.annualPaymentEstimateFrom,
    annualPaymentTo: listScope.annualPaymentEstimateTo,
    actionRequired: listScope.actionRequired,
    actionType: toSubscriptionActionType(listScope.actionTypes),
  };
};

export const useSubscriptionsBySoftwareIds = (
  softwareIds: string[],
  listScope?: ProductListScopeFilter,
) =>
  useQueries({
    queries: softwareIds.map(softwareId => {
      const params = buildSubscriptionListParamsForProductScope(softwareId, listScope);
      return {
        queryKey: sbQueryKey.list(params),
        queryFn: async () => {
          const result = await sbRepo.getList(params);
          return result.content.map(dto => new SubscriptionListModel(dto));
        },
        staleTime: SUBSCRIPTIONS_BY_SOFTWARE_STALE_TIME_MS,
      };
    }),
  });

export const useGetMySubscriptions = (
  options?: Omit<UseQueryOptions<Page<SubscriptionList>, AxiosError, Page<SubscriptionListModel>>, 'queryKey' | 'queryFn' | 'select'>,
) => {
  const selectMySubscriptions = useCallback(
    (data: Page<SubscriptionList>): Page<SubscriptionListModel> => ({
      ...data,
      content: data.content.map(sb => new SubscriptionListModel(sb)),
    }),
    [],
  );

  const result = useQuery<Page<SubscriptionList>, AxiosError, Page<SubscriptionListModel>>({
    queryKey: ['my-subscriptions'],
    queryFn: () => sbRepo.getMySubscriptions(),
    select: selectMySubscriptions,
    ...options,
  });
  return { ...result };
};

export const useGetSubscription = (
  id: string,
  options?: Omit<UseQueryOptions<Subscription, AxiosError, SubscriptionModel>, 'queryKey' | 'queryFn' | 'select'>,
) => {
  const selectSubscription = useCallback(
    (data: Subscription) => new SubscriptionModel(data),
    [],
  );

  return useQuery<Subscription, AxiosError, SubscriptionModel>({
    queryKey: sbQueryKey.detail(id),
    queryFn: () => sbRepo.getOne(id),
    select: selectSubscription,
    ...options,
    cacheTime: 50000,
    staleTime: 30000,
    enabled: QueryUtil.withRequired(!!id, options?.enabled),
  });
};

export const useGetSubscriptions = (id: string[], options?: UseQueryOptions<SubscriptionModel, AxiosError>) =>
  useQueries({
    queries: id.map(subscriptionId => ({
      queryKey: sbQueryKey.detail(subscriptionId),
      queryFn: async () => {
        const result = await sbRepo.getOne(subscriptionId);
        return new SubscriptionModel(result);
      },
      ...options,
      cacheTime: 50000,
      staleTime: 30000,
      enabled: QueryUtil.withRequired(!!id, options?.enabled),
    })),
  });

export const useGetSubscriptionUsersInfinite = (
  subscriptionId: SubscriptionId,
  queries?: Omit<SubscriptionUserFilter, 'page'>,
  options?: UseInfiniteQueryOptions<Page<SubscriptionUserModel>>,
) =>
  useInfiniteQuery({
    queryKey: subscriptionUserQueryKey.listInfinite(subscriptionId, queries ?? {}),
    queryFn: async ({ pageParam = 0 }) => {
      const result = await sbRepo.getUserList(subscriptionId, queries ? { ...queries, page: pageParam } : {});
      return {
        ...result,
        content: result.content.map(dto => new SubscriptionUserModel(dto, subscriptionId)),
        pageParam,
      };
    },
    cacheTime: 100 * 60 * 1000,
    staleTime: 10 * 60 * 1000,
    ...options,
    getNextPageParam: lastPage => (lastPage.last ? undefined : lastPage.number + 1),
  });

export const useGetSubscriptionUserStat = (
  subscriptionId: string,
  options?: Omit<UseQueryOptions<SubscriptionUserStatDTO, AxiosError, SubscriptionUserStatModel>, 'queryKey' | 'queryFn' | 'select'>,
) => {
  const selectUserStat = useCallback(
    (data: SubscriptionUserStatDTO) => new SubscriptionUserStatModel(data),
    [],
  );

  return useQuery<SubscriptionUserStatDTO, AxiosError, SubscriptionUserStatModel>({
    queryKey: sbQueryKey.userStatus(subscriptionId),
    queryFn: () => sbRepo.getUserStatus(subscriptionId),
    select: selectUserStat,
    ...options,
  });
};

export const useGetSubscriptionDailyUsage = (
  subscriptionId: string,
  queries: SubscriptionDailyUsageFilter,
  options?: Omit<UseQueryOptions<Page<DailyUsageDTO>, AxiosError, Page<DailyUsageModel>>, 'queryKey' | 'queryFn' | 'select'>,
) => {
  const selectDailyUsage = useCallback(
    (data: Page<DailyUsageDTO>): Page<DailyUsageModel> => ({
      ...data,
      content: data.content.map(dto => new DailyUsageModel(dto)),
    }),
    [],
  );

  const result = useQuery<Page<DailyUsageDTO>, AxiosError, Page<DailyUsageModel>>({
    queryKey: sbQueryKey.dailyUsage(subscriptionId, queries),
    queryFn: () => sbRepo.getDailyUsage(subscriptionId, queries),
    select: selectDailyUsage,
    ...options,
  });

  const initialData = useMemo(() => ({ content: [] as DailyUsageModel[] }), []);
  return { ...result, data: result.data ?? initialData };
};

export const useGetSubscriptionUser = (
  subscriptionId: string,
  userLoginId: string,
  options?: Omit<UseQueryOptions<SubscriptionUserDTO, AxiosError, SubscriptionUserModel>, 'queryKey' | 'queryFn' | 'select'>,
) => {
  const selectUser = useCallback(
    (data: SubscriptionUserDTO) => new SubscriptionUserModel(data, subscriptionId),
    [subscriptionId],
  );

  return useQuery<SubscriptionUserDTO, AxiosError, SubscriptionUserModel>({
    queryKey: subscriptionUserQueryKey.detail(subscriptionId, userLoginId),
    queryFn: () => sbRepo.getSubscriptionUser(subscriptionId, userLoginId),
    select: selectUser,
    enabled: !!subscriptionId && !!userLoginId && (options?.enabled !== undefined ? options.enabled : true),
  });
};

export const useGetSubscriptionMonthlyUsage = (
  subscriptionId: string,
  queries: SubscriptionMonthlyUsageFilter,
  options?: Omit<UseQueryOptions<Page<MonthlyUsageDTO>, AxiosError, Page<MonthlyUsageModel>>, 'queryKey' | 'queryFn' | 'select'>,
) => {
  const selectMonthlyUsage = useCallback(
    (data: Page<MonthlyUsageDTO>): Page<MonthlyUsageModel> => ({
      ...data,
      content: data.content.map(dto => new MonthlyUsageModel(dto)),
    }),
    [],
  );

  const result = useQuery<Page<MonthlyUsageDTO>, AxiosError, Page<MonthlyUsageModel>>({
    queryKey: sbQueryKey.monthlyUsage(subscriptionId, queries),
    queryFn: () => sbRepo.getMonthlyUsage(subscriptionId, queries),
    select: selectMonthlyUsage,
    ...options,
  });

  const initialData = useMemo(() => ({ content: [] as MonthlyUsageModel[] }), []);
  return { ...result, data: result.data ?? initialData };
};

export const useGetUsersActivityStats = (
  subscriptionId: string,
  options?: UseQueryOptions<SubscriptionUsersActivityStats, AxiosError>,
) =>
  useQuery({
    queryKey: sbQueryKey.usersActivityStats(subscriptionId),
    queryFn: async () => {
      const result = await sbRepo.getUsersActivityStats(subscriptionId);
      return result;
    },
    ...options,
  });

export const useGetSubscriptionMonthlyUsers = (
  subscriptionId: string,
  filter: SubscriptionMonthlyUserFilter,
  options?: Omit<UseQueryOptions<Page<MonthlySubscriptionUserDTO>, AxiosError, Page<MonthlySubscriptionUserModel>>, 'queryKey' | 'queryFn' | 'select'>,
) => {
  const selectMonthlyUsers = useCallback(
    (data: Page<MonthlySubscriptionUserDTO>): Page<MonthlySubscriptionUserModel> => ({
      ...data,
      content: data.content.map(dto => new MonthlySubscriptionUserModel(dto)),
    }),
    [],
  );

  return useQuery<Page<MonthlySubscriptionUserDTO>, AxiosError, Page<MonthlySubscriptionUserModel>>({
    queryKey: sbQueryKey.monthlyUser(subscriptionId, filter),
    queryFn: () => sbRepo.getMonthlyUser(subscriptionId, filter),
    select: selectMonthlyUsers,
    ...options,
  });
};

export const useAddExternalSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ subscription, files }: { subscription: ExternalSubscriptionCreateRq; files?: File[] }) => {
      const result = await sbRepo.createExternal(subscription, files);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sbQueryKey.lists() });
    },
  });
};

export const useAddSubscriptionUser = () => {
  const queryClient = useQueryClient();

  const result = useMutation(
    async (rqData: SubscriptionUserCreateRq) => {
      const result = await sbRepo.addUser(rqData.subscriptionId, rqData);
      return result;
    },
    {
      onSuccess: result => {
        queryClient.invalidateQueries({
          queryKey: subscriptionUserQueryKey.lists(),
        });
        queryClient.invalidateQueries({
          queryKey: tenantMemberQueryKey.lists(),
        });
        queryClient.invalidateQueries({
          queryKey: sbQueryKey.userStatus(result.subscriptionId),
        });
      },
    },
  );

  return { ...result };
};

export const useUpdateUserSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (rqData: UserSubscriptionUpdateDTO) => {
      const result = await sbRepo.updateUserSubscription(rqData.id, rqData);
      return result;
    },
    onSuccess: updated => {
      queryClient.invalidateQueries({
        queryKey: sbQueryKey.detail(updated.id),
      });
      queryClient.invalidateQueries({ queryKey: sbQueryKey.lists() });
    },
  });
};

export const useUpdateSubscriptionUser = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ subscriptionId, rqData }: { subscriptionId: string; rqData: SubscriptionUserUpdateRq }) => {
      await sbRepo.updateUser(subscriptionId, rqData.userLoginId, rqData);
      return { subscriptionId, userLoginId: rqData.userLoginId };
    },
    {
      onSuccess: ({ subscriptionId, userLoginId }) => {
        queryClient.invalidateQueries({
          queryKey: subscriptionUserQueryKey.detail(subscriptionId, userLoginId),
        });
        queryClient.invalidateQueries({
          queryKey: subscriptionUserQueryKey.lists(),
        });
      },
    },
  );
};

export const useUpdateSubscriptionMemo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ subscriptionId, memo }: { subscriptionId: string } & SubscriptionMemoUpdateDTO) => {
      await sbRepo.updateSubscriptionMemo(subscriptionId, { memo });
      return subscriptionId;
    },
    onSuccess: subscriptionId => {
      queryClient.invalidateQueries({
        queryKey: sbQueryKey.detail(subscriptionId),
      });
    },
  });
};

export const useDeleteSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (subscriptionId: string) => {
      await sbRepo.delete(subscriptionId);
      return subscriptionId;
    },
    onSuccess: subscriptionId => {
      queryClient.invalidateQueries({ queryKey: sbQueryKey.lists() });
      queryClient.removeQueries({
        queryKey: sbQueryKey.detail(subscriptionId),
      });
    },
  });
};

export const useDeleteSubscriptionUser = () => {
  const queryClient = useQueryClient();

  const result = useMutation(
    async ({
      subscriptionId,
      userLoginIdOrUserId,
    }: {
      subscriptionId: SubscriptionId;
      userLoginIdOrUserId: string;
    }): Promise<{
      subscriptionId: SubscriptionId;
      userLoginIdOrUserId: string;
    }> => {
      await sbRepo.deleteUser(subscriptionId, userLoginIdOrUserId);
      return { subscriptionId, userLoginIdOrUserId };
    },
    {
      onSuccess: ({ subscriptionId }) => {
        queryClient.invalidateQueries({
          queryKey: subscriptionUserQueryKey.lists(),
        });
        queryClient.invalidateQueries({ queryKey: sbQueryKey.lists() });
        queryClient.invalidateQueries({
          queryKey: sbQueryKey.userStatus(subscriptionId),
        });
        queryClient.invalidateQueries({ queryKey: tenantMemberQueryKey.lists() });
        // 라이선스 회수 화면·구독 사용자 상세(useSubscriptionUserLicenses) 데이터 갱신
        queryClient.invalidateQueries({ queryKey: ['userLicenses'] });
      },
    },
  );

  return { ...result };
};

export const useAddRemoteUser = () => {
  const queryClient = useQueryClient();

  type MutationVariable = {
    subscriptionId: string;
    raData: RemoteSubscrptionUserCreateDTO;
  };

  return useMutation({
    mutationFn: async (variable: MutationVariable) => {
      const result = await sbRepo.addRemoteUser(variable.subscriptionId, variable.raData);
      return result;
    },
    onSuccess: result => {
      queryClient.invalidateQueries({ queryKey: subscriptionUserQueryKey.all });
      queryClient.invalidateQueries({
        queryKey: sbQueryKey.userStatus(result.subscriptionId),
      });
    },
  });
};

export const useFileUpload = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      subscriptionId,
      files,
      config,
    }: {
      subscriptionId: string;
      files: Blob[];
      config?: AxiosRequestConfig;
    }) => {
      await sbRepo.fileUpload({ subscriptionId, files }, config);
      return subscriptionId;
    },
    onSuccess: subscriptionId => {
      queryClient.invalidateQueries({
        queryKey: sbQueryKey.detail(subscriptionId),
      });
    },
  });
};

export const useFileDelete = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ subscriptionId, fileId }: { subscriptionId: string; fileId: string }) => {
      await sbRepo.fileDelete(subscriptionId, fileId);
      return subscriptionId;
    },
    onSuccess: subscriptionId => {
      queryClient.invalidateQueries({
        queryKey: sbQueryKey.detail(subscriptionId),
      });
    },
  });
};

export const useActivateUsageWorkflow = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (subscriptionId: string) => {
      const result = await sbRepo.activateUsageWorkflow(subscriptionId);
      return { subscriptionId, usageWorkflow: result };
    },
    onSuccess: ({ subscriptionId }) => {
      queryClient.invalidateQueries({
        queryKey: sbQueryKey.detail(subscriptionId),
      });
      queryClient.invalidateQueries({ queryKey: sbQueryKey.lists() });
    },
  });
};

export const useDeactivateUsageWorkflow = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (subscriptionId: string) => {
      const result = await sbRepo.deactivateUsageWorkflow(subscriptionId);
      return { subscriptionId, usageWorkflow: result };
    },
    onSuccess: ({ subscriptionId }) => {
      queryClient.invalidateQueries({
        queryKey: sbQueryKey.detail(subscriptionId),
      });
      queryClient.invalidateQueries({ queryKey: sbQueryKey.lists() });
    },
  });
};

export const useExecuteUsageWorkflow = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (subscriptionId: string) => {
      const result = await sbRepo.executeUsageWorkflow(subscriptionId);
      return { subscriptionId, usageWorkflow: result };
    },
    onSuccess: ({ subscriptionId }) => {
      queryClient.invalidateQueries({
        queryKey: sbQueryKey.detail(subscriptionId),
      });
    },
  });
};
